import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import CardDropdown from "components/common/CardDropdown";
import FalconPagination from "components/common/custom/pagination";
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import React, { useEffect, useState, useCallback } from "react";
import { Card, Dropdown } from "react-bootstrap";
import { Ban, Check, Gear, Pen } from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import SubtleBadge from "components/common/SubtleBadge";
import IconButton from "components/common/IconButton";

const InsuranceCompanyList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [allData, setAllData] = useState([]);
  const [data, setData] = useState([]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handlePerRowsChange = useCallback((newPerPage) => {
    setRowsPerPage(newPerPage);
    setPage(1);
  }, []);

  const setting = { edit: true };

  const columns = [
    {
      accessorKey: "index",
      header: "#",
      cell: ({ row }) => (
        <div className="d-flex align-items-center w-100">
          <div
            className="rounded-circle bg-200 d-flex flex-center fs-11"
            style={{ width: "30px", height: "30px" }}
          >
            {(page - 1) * rowsPerPage + row.index + 1}
          </div>
        </div>
      ),
    },
    {
      accessorKey: "payer",
      header: "Payer ",
      cell: ({ row: { original } }) => `${original?.payer || ""}`,
    },
    {
      accessorKey: "code",
      header: "Code",
      cell: ({ row: { original } }) => `${original?.code || ""}`,
    },
    {
      accessorKey: "payer_name",
      header: "Payer Name",
      cell: ({ row: { original } }) => `${original?.payer_name || ""}`,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row: { original } }) => {
        const rawStatus = original.status || "";
        const normalizedStatus = rawStatus.replace(/\s+/g, "").toLowerCase();
        const statusConfig = {
          active: {
            bg: "#d9f8eb",
            color: "#00894f",
            icon: "check",
            label: "Active",
          },
          deactive: {
            bg: "#fbdbe1",
            color: "#b82c46",
            icon: "exclamation",
            label: "Deactive",
          },
        };
        const config = statusConfig[normalizedStatus] || {
          bg: "#f8f9fa",
          color: "#000",
          icon: "question",
          label: rawStatus || "Unknown",
        };
        return (
          <div className="d-flex justify-content-start w-100">
            <SubtleBadge
              pill
              style={{
                backgroundColor: config.bg,
                color: config.color,
                border: `1px solid ${config.color}20`,
                width: "110px",
              }}
              className="d-flex flex-center fs-12 p-2 gap-1"
            >
              <FontAwesomeIcon
                icon={config.icon}
                transform="shrink-2"
                className="ms-1 me-1"
              />
              {config.label}
            </SubtleBadge>
          </div>
        );
      },
    },
    {
      accessorKey: "action",
      header: <Gear />,
      cell: ({ row: { original } }) => (
        <>
          {original?.status?.toLowerCase() === "active" ? (
            <IconButton size="sm">
              <Ban />
            </IconButton>
          ) : (
            <IconButton size="sm">
              <Check />
            </IconButton>
          )}
        </>
      ),
    },
  ];

  const table = useAdvanceTable({
    data: [],
    columns,
    manualPagination: true,
    pageCount: Math.ceil(totalRows / rowsPerPage),
    state: {
      pagination: {
        pageIndex: page - 1,
        pageSize: rowsPerPage,
      },
    },
  });

  useEffect(() => {
    table.setPagination({ pageIndex: page - 1, pageSize: rowsPerPage });
  }, [page, rowsPerPage]);

  return (
    <div className="mb-3 mt-3 border">
      {data?.length > 0 ? (
        <div>
          <AdvanceTableProvider {...table}>
            <AdvanceTable
              tableProps={{
                size: "sm",
                className: "fs-10 mb-0 overflow-hidden",
                striped: true,
              }}
              headerClassName="bg-200 text-nowrap align-middle"
              rowClassName="align-middle white-space-nowrap"
            />
          </AdvanceTableProvider>
          <Card.Footer className="d-flex mt-2 mb-3 justify-content-end align-items-center">
            <FalconPagination
              currentPage={page}
              rowsPerPage={rowsPerPage}
              totalRecords={totalRows}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePerRowsChange}
            />
          </Card.Footer>
        </div>
      ) : (
        <div className="text-center py-3">No Data To Show</div>
      )}
    </div>
  );
};

export default InsuranceCompanyList;
